import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import usePageData from '../helpers/usePageData';
import { API_URL } from '../helpers/api';
import DOMPurify from 'dompurify';

// Anciens contenus en texte brut : on conserve les retours à la ligne
const toHtml = (content) => {
  const html = /<[a-z][\s\S]*>/i.test(content)
    ? content
    : content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>');
  return DOMPurify.sanitize(html);
};

const SpecialMenuPage = () => {
  const { data: rawData, loading } = usePageData('menu-special');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  if (loading || !rawData) {
    return null;
  }

  // Migration: support old single image format
  const pageData = { ...rawData };
  if (pageData.image && !pageData.images) {
    pageData.images = [pageData.image];
  }
  const images = pageData.images || [];
  const hasImages = images.length > 0;

  const openViewer = (index) => {
    setSelectedImageIndex(index);
    setIsViewerOpen(true);
  };

  const navigateImage = (direction) => {
    const total = images.length;
    if (direction === 'next') {
      setSelectedImageIndex((selectedImageIndex + 1) % total);
    } else {
      setSelectedImageIndex((selectedImageIndex - 1 + total) % total);
    }
  };

  return (
    <div className="special-menu-page">
      {/* Lightbox Modal */}
      {hasImages && (
        <div
          className={`modal fade ${isViewerOpen ? 'show d-block' : ''}`}
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 2000 }}
          onClick={() => setIsViewerOpen(false)}
        >
          <div className="modal-dialog modal-fullscreen">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0">
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setIsViewerOpen(false)}
                  aria-label="Fermer"
                />
              </div>
              <div
                className="modal-body d-flex justify-content-center align-items-center position-relative"
                onClick={e => e.stopPropagation()}
              >
                {images.length > 1 && (
                  <button
                    className="btn btn-link text-white position-absolute start-0 top-50 translate-middle-y"
                    onClick={() => navigateImage('prev')}
                  >
                    <ChevronLeft size={48} />
                  </button>
                )}
                <img
                  src={`${API_URL}${images[selectedImageIndex]?.url}`}
                  alt={images[selectedImageIndex]?.name || `Image ${selectedImageIndex + 1}`}
                  className="img-fluid"
                  style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
                />
                {images.length > 1 && (
                  <button
                    className="btn btn-link text-white position-absolute end-0 top-50 translate-middle-y"
                    onClick={() => navigateImage('next')}
                  >
                    <ChevronRight size={48} />
                  </button>
                )}
              </div>
              {images.length > 1 && (
                <div className="modal-footer border-0 justify-content-center text-white">
                  <span className="small">{selectedImageIndex + 1} / {images.length}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div
        className="position-relative w-100 overflow-hidden pt-5"
        style={{
          minHeight: '50vh',
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%)',
        }}
      >
        <div
          className="d-flex flex-column justify-content-center align-items-center text-white h-100 px-4 text-center"
          style={{ minHeight: '50vh' }}
        >
          <span className="text-uppercase fw-bold mb-3" style={{ letterSpacing: '3px', fontSize: '0.9rem', opacity: 0.9 }}>
            Exposition
          </span>
          <h1 className="display-4 fw-bold mb-3 lh-sm" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.3)', maxWidth: '800px' }}>
            {pageData.hero?.title}
          </h1>
          {pageData.hero?.subtitle && (
            <p className="fs-5 mx-auto mb-3 fw-light" style={{ maxWidth: '700px', opacity: 0.95, whiteSpace: 'pre-line' }}>
              {pageData.hero.subtitle}
            </p>
          )}
          {pageData.dates && (
            <div className="mt-3">
              <span className="bg-warning text-dark px-4 py-2 rounded-pill fw-bold">
                {pageData.dates}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main content: images (left) + text (right) */}
      <section className="py-5 bg-white">
        <div className="container py-3">
          <div className="row g-4 g-lg-5">
            {hasImages && (
              <div className="col-lg-6">
                <div className="d-flex flex-column gap-3">
                  {images.map((img, index) => (
                    <div
                      key={index}
                      className="rounded shadow overflow-hidden"
                      style={{ cursor: 'pointer' }}
                      onClick={() => openViewer(index)}
                    >
                      <img
                        src={`${API_URL}${img.url}`}
                        alt={img.name || `Image ${index + 1}`}
                        className="d-block w-100 h-auto"
                        style={{ transition: 'transform 0.3s ease' }}
                        onMouseOver={e => e.target.style.transform = 'scale(1.03)'}
                        onMouseOut={e => e.target.style.transform = 'scale(1)'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className={hasImages ? 'col-lg-6' : 'col-lg-8 mx-auto'}>
              {pageData.sections?.map((section, index) => (
                <div key={index} className={index > 0 ? 'mt-4' : ''}>
                  {section.title && (
                    <h2 className="display-6 fw-bold mb-3">{section.title}</h2>
                  )}
                  {section.content && (
                    <div className="fs-5 text-muted lh-lg" dangerouslySetInnerHTML={{ __html: toHtml(section.content) }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Artists Section */}
      {pageData.artists && (
        <section className="py-5 bg-light">
          <div className="container py-3">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <h2 className="display-6 fw-bold mb-4">Les artistes</h2>
                <p className="fs-5 text-muted lh-lg" style={{ whiteSpace: 'pre-line' }}>
                  {pageData.artists}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SpecialMenuPage;
